import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { initialsOf } from "../../_shared/QokrPrimitives/QokrPrimitives";
import { LR_ROLE } from "../_socket/quarterlyOkrLiveReviewSocketStore";
import "./_live-presence.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Who's in the room. EXW's presence chips were built for two people (CEO and
// coach); a quarterly live review has the whole team, so a chip per person
// fills the header and wraps. Instead:
//
//   • the header trigger — a face pile of the people here (capped, "+N") and
//     "3 of 8 here". One row whatever the team size.
//   • a popover with everyone, "Here now" then "Not here": face with a status
//     dot, name (+ You), role, "Can edit" for the CEO and coach, and whether
//     someone left or never joined.
//
// The roster is the run's members (`details.members`) plus the coach, with the
// live state from `presence_update` (`participants`) laid over it — presence
// only lists people who have connected, so it can't say who hasn't joined.
//
// ⚠️ SHARED WITH THE OTHER APP at the same folder depth — keep both copies equal.
// ═══════════════════════════════════════════════════════════════════════════

const MAX_FACES = 5;
const POP_WIDTH = 320;
const GAP = 8;
const PAD = 12;

const ROLE_LABEL = {
  [LR_ROLE.CEO]: "CEO",
  [LR_ROLE.COACH]: "Coach",
  [LR_ROLE.MEMBER]: "Team member",
};
const RANK = { [LR_ROLE.COACH]: 1, [LR_ROLE.CEO]: 2, [LR_ROLE.MEMBER]: 3 };
const canEdit = (role) => role === LR_ROLE.CEO || role === LR_ROLE.COACH;

// One entry per person: `{ id, name, role, title, state: here|left|not_joined, you }`.
const buildRoster = ({ participants, members, viewerRole, viewerId }) => {
  const byId = new Map(participants.map((p) => [String(p.user_id), p]));
  const seen = new Set();
  const entry = (id, name, role, title) => {
    const live = id ? byId.get(id) : null;
    if (id) seen.add(id);
    return {
      id: id || role,
      name: live?.name || name,
      role,
      title,
      state: live ? (live.connected ? "here" : "left") : "not_joined",
      you:
        role === viewerRole && canEdit(role)
          ? true
          : Boolean(viewerId) && String(viewerId) === id,
    };
  };

  const coach = participants.find((p) => p.role === LR_ROLE.COACH);
  const roster = [
    entry(coach ? String(coach.user_id) : null, "Your coach", LR_ROLE.COACH),
  ];
  members.forEach((m) => {
    const id = String(m.user_id);
    const role =
      byId.get(id)?.role || (m.is_ceo ? LR_ROLE.CEO : LR_ROLE.MEMBER);
    roster.push(entry(id, m.name, role, m.role));
  });
  // Anyone connected who isn't on the run's member list (e.g. the CEO when
  // the list leaves them out) still shows.
  participants.forEach((p) => {
    const id = String(p.user_id);
    if (!seen.has(id)) roster.push(entry(id, p.name, p.role));
  });

  return roster.sort(
    (a, b) =>
      Number(b.you) - Number(a.you) ||
      RANK[a.role] - RANK[b.role] ||
      String(a.name).localeCompare(String(b.name)),
  );
};

const Face = ({ person, size = "md", style }) => (
  <span
    className={`qokr-face qokr-face--${size}${
      person.role === LR_ROLE.COACH ? " is-coach" : ""
    }${person.state === "here" ? "" : " is-away"}`}
    style={style}
    aria-hidden="true"
  >
    {initialsOf(person.name)}
    <span
      className={`qokr-face__dot${person.state === "here" ? " is-on" : ""}`}
    />
  </span>
);

const STATE_NOTE = { left: "Left the session", not_joined: "Not joined yet" };

const RosterRow = ({ person }) => (
  <li
    className={`qokr-roster__row${person.state === "here" ? "" : " is-away"}`}
  >
    <Face person={person} />
    <span className="qokr-roster__who">
      <span className="qokr-roster__name">
        <span className="qokr-roster__name-text">{person.name}</span>
        {person.you ? <span className="qokr-roster__you">You</span> : null}
      </span>
      <span className="qokr-roster__role">
        {STATE_NOTE[person.state] ? (
          <>
            <span className="qokr-roster__note">
              {STATE_NOTE[person.state]}
            </span>
            {" · "}
          </>
        ) : null}
        {person.role === LR_ROLE.MEMBER && person.title
          ? person.title
          : ROLE_LABEL[person.role]}
      </span>
    </span>
    {canEdit(person.role) ? (
      <span className="qokr-roster__tag" title="Can edit and lock">
        <i className="fa-solid fa-pen" aria-hidden="true" />
        Can edit
      </span>
    ) : null}
  </li>
);

const RosterPopover = ({ anchorRef, onClose, here, away }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // Portalled and fixed, so the header's `overflow: hidden` can't clip it.
  useLayoutEffect(() => {
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({
        top: r.bottom + GAP,
        left: Math.max(
          PAD,
          Math.min(r.left, window.innerWidth - POP_WIDTH - PAD),
        ),
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      onClose();
      anchorRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="qokr-roster"
      role="dialog"
      aria-label="People in this live review"
      style={{
        width: POP_WIDTH,
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="qokr-roster__head">
        <span className="qokr-roster__title">In this live review</span>
        <span className="qokr-roster__total">
          {here.length} of {here.length + away.length}
        </span>
      </div>
      <div className="qokr-roster__body">
        {[
          ["here", "Here now", here],
          ["away", "Not here", away],
        ].map(([key, label, people]) =>
          people.length ? (
            <div className="qokr-roster__group" key={key}>
              <span className="qokr-roster__label">
                <span
                  className={`qokr-roster__label-dot${key === "here" ? " is-on" : ""}`}
                />
                {label} · {people.length}
              </span>
              <ul className="qokr-roster__list">
                {people.map((person) => (
                  <RosterRow key={person.id} person={person} />
                ))}
              </ul>
            </div>
          ) : null,
        )}
      </div>
      <div className="qokr-roster__foot">
        <i className="fa-regular fa-circle-question" aria-hidden="true" />
        The CEO and coach edit and lock; everyone else follows along.
      </div>
    </div>,
    document.body,
  );
};

const LivePresence = ({
  participants = [],
  members = [],
  viewerRole,
  viewerId = null,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const roster = buildRoster({ participants, members, viewerRole, viewerId });
  const here = roster.filter((p) => p.state === "here");
  const away = roster.filter((p) => p.state !== "here");
  const faces = here.slice(0, MAX_FACES);
  const extra = here.length - faces.length;
  const label = `${here.length} of ${roster.length} here`;

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        className={`qokr-presence-btn${open ? " is-open" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}. Show everyone in this live review`}
        onClick={() => setOpen((v) => !v)}
      >
        {faces.length ? (
          <span className="qokr-presence-btn__faces">
            {faces.map((person, i) => (
              <Face
                key={person.id}
                person={person}
                size="sm"
                style={{ zIndex: faces.length - i + 1 }}
              />
            ))}
            {extra > 0 ? (
              <span
                className="qokr-face qokr-face--sm qokr-face--more"
                aria-hidden="true"
              >
                +{extra}
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="qokr-presence-btn__text">
          <span className="qokr-presence-btn__live" aria-hidden="true" />
          {label}
        </span>
        <i
          className={`fa-solid fa-chevron-down qokr-presence-btn__chev${
            open ? " is-open" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <RosterPopover
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
          here={here}
          away={away}
        />
      ) : null}
    </>
  );
};

export default LivePresence;
